/**
 * 我的成長卡 — home / learn-portal 共用的「今日修煉值 / 連續登入 / 最新解鎖」摘要。
 *
 * 學術風格(boss 2026-06-08 拍板,見 wiki page-470f59c0 / she-ji-xi-tong):
 *   紙片卡（`--ef-paper-bright`)、墨水字（`--ef-ink`/`--ef-ink-soft`/`--ef-sub`)、
 *   學術綠重點（`--ef-green`)、細紙線分隔（`--ef-line`)。取代舊夜空藍漸層 +
 *   橘黃數字。色票一律取自 `theme.ts` 的 `--ef-*` token,不自編色。
 *
 * 相容性:保留 `.my-progress-card` / `.mp-grid` / `.mp-stat` / `.mp-label` /
 *   `.mp-value` / `.mp-small` class（UI inspection / 既有慣例依賴),但外觀改由
 *   本檔 inline `--ef-*` 樣式決定,**不改全域 styles.css**。對外 API
 *   (`renderMyProgressCard(container)`)與所有資料/數字/節點結構不變。
 */
import { loadProgress } from '../shared/progress';
import { SKILL_CATALOG } from '../shared/skills';
import { computeCombatPower } from '../game/progression/combat-power';
import { ensureAcademicTheme } from './ui/theme';

export function renderMyProgressCard(container: HTMLElement): void {
  // 確保 `--ef-*` token 已注入(home.ts 不一定先呼;冪等)。
  ensureAcademicTheme();

  const progress = loadProgress();
  if (!progress) return;
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayXP = progress.dailyXP?.[todayKey] ?? 0;
  const latestUnlock = [...progress.unlockedSkills].slice(-1)[0];
  const skill = latestUnlock ? SKILL_CATALOG.find(s => s.id === latestUnlock) : null;
  // 戰力分數(R-004 A2)— 學習驅動,單調隨解鎖/升級成長。唯讀,不寫 progress。
  const combatPower = computeCombatPower(progress);

  const card = document.createElement('div');
  card.className = 'my-progress-card';
  // 紙片卡:亮紙底 + 細紙線框 + 極淺陰影(學術風,非 material 重陰影)。
  card.style.cssText = [
    'background:var(--ef-paper-bright)',
    'border:1px solid var(--ef-line)',
    'border-radius:var(--ef-radius)',
    'padding:18px 20px',
    'margin-bottom:24px',
    'max-width:600px',
    'box-shadow:var(--ef-shadow-card)',
    'font-family:var(--ef-sans)',
  ].join(';');

  card.innerHTML = `
    <h3 style="font-family:var(--ef-serif);font-weight:700;font-size:17px;color:var(--ef-ink);margin:0 0 14px;letter-spacing:0.5px;padding-bottom:10px;border-bottom:1px solid var(--ef-line);">我的成長</h3>
    <div class="mp-power" data-testid="combat-power" style="display:flex;align-items:baseline;justify-content:center;gap:8px;margin-bottom:14px;padding-bottom:12px;border-bottom:1px solid var(--ef-line);">
      <span class="mp-power-label" style="font-size:13px;color:var(--ef-sub);">⚡ 戰力</span>
      <span class="mp-power-value" style="font-family:var(--ef-serif);font-size:30px;font-weight:700;color:var(--ef-green);font-variant-numeric:tabular-nums;line-height:1;">${combatPower}</span>
    </div>
    <div class="mp-grid" style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;">
      <div class="mp-stat" style="text-align:center;">
        <div class="mp-label" style="font-size:13px;color:var(--ef-sub);">今日修煉值</div>
        <div class="mp-value" style="font-family:var(--ef-serif);font-size:24px;font-weight:700;color:var(--ef-green);margin-top:4px;font-variant-numeric:tabular-nums;">+${Math.round(todayXP)}</div>
      </div>
      <div class="mp-stat" style="text-align:center;">
        <div class="mp-label" style="font-size:13px;color:var(--ef-sub);">連續登入</div>
        <div class="mp-value" style="font-family:var(--ef-serif);font-size:24px;font-weight:700;color:var(--ef-green);margin-top:4px;font-variant-numeric:tabular-nums;">${progress.streak?.days ?? 0} 天</div>
      </div>
      <div class="mp-stat" style="text-align:center;">
        <div class="mp-label" style="font-size:13px;color:var(--ef-sub);">最新解鎖</div>
        <div class="mp-value mp-small" style="font-family:var(--ef-serif);font-size:15px;font-weight:600;color:var(--ef-ink-soft);margin-top:4px;line-height:1.4;">${skill?.displayName ?? '尚無'}</div>
      </div>
    </div>
  `;
  container.appendChild(card);
}
